import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { ChevronUp, ChevronDown, LogOut, MapPin, ArrowUp, ArrowDown, X, Layers, FolderOpen } from 'lucide-react';
import { useBoreholeBrowser } from '../hooks/useBoreholeBrowser';
import { useAuthContext } from '../contexts/AuthContext';
import { pl2000ToWGS84 } from '../utils/coordConvert';
import { createProject } from '../firebase/projects';
import { readPendingHandoff, clearPendingHandoff } from '../firebase/handoff';

// Formatuje lastModifyTs (sekundy/Firestore Timestamp w sekundach) na YYYY-MM-DD HH:MM
const formatVersionTs = (ts) => {
  if (!ts) return '(brak daty)';
  const date = new Date(ts * 1000);
  if (Number.isNaN(date.getTime())) return '(brak daty)';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

// Dropdown wyboru wersji UserData dla otworu w tabeli "Wybrane otwory".
// Domyślnie najnowsza (selectedVersionId === null). Lista lazy-loadowana na focus/klik.
function VersionPicker({ boreholeId, selectedVersionId, versionsState, onOpen, onChange }) {
  const versions = versionsState?.versions;
  const loading = versionsState?.loading;
  const error = versionsState?.error;

  const handleOpen = () => {
    if (!versions && !loading) onOpen();
  };

  const handleChange = (e) => {
    const val = e.target.value;
    onChange(val === '__newest__' ? null : val);
  };

  const value = selectedVersionId || '__newest__';
  const isOverride = !!selectedVersionId;

  return (
    <div className="flex items-center gap-1">
      <select
        value={value}
        onMouseDown={handleOpen}
        onFocus={handleOpen}
        onChange={handleChange}
        className={`w-full border rounded px-1.5 py-0.5 text-xs ${isOverride ? 'border-orange-300 bg-orange-50' : ''}`}
        title={isOverride ? 'Wybrana konkretna wersja UserData' : 'Domyślnie najnowsza wersja'}
      >
        <option value="__newest__">Najnowsza{loading ? ' (ładuję…)' : ''}</option>
        {versions && versions.map(v => (
          <option key={v.id} value={v.id}>
            {(v.userName || '(brak nazwy)')} — {formatVersionTs(v.lastModifyTs)}
          </option>
        ))}
      </select>
      {isOverride && (
        <button
          type="button"
          onClick={() => onChange(null)}
          className="text-gray-400 hover:text-gray-600"
          title="Wróć do najnowszej"
        >
          <X size={12} />
        </button>
      )}
      {error && <span className="text-xs text-red-500" title={error}>!</span>}
    </div>
  );
}

export default function BoreholeBrowserPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, logout } = useAuthContext();
  const pendingProject = location.state?.pendingProject;
  const handoffFromState = location.state?.handoffData;
  const handoffFlag = searchParams.get('handoff');

  const {
    projects,
    loadingProjects,
    projectsError,
    projectsLoaded,
    loadProjects,
    refreshProjects,
    projectFilter, setProjectFilter,
    selectedProject,
    selectProject,
    boreholes,
    allBoreholes,
    loadingBoreholes,
    boreholesError,
    boreholeFilter, setBoreholeFilter,
    selectedBoreholeIds,
    orderedSelection,
    computedDistances,
    toggleBoreholeSelection,
    clearSelection,
    moveBoreholeUp,
    moveBoreholeDown,
    setBoreholeDistance,
    setBoreholeUserDataVersion,
    versionsByBoreholeId,
    loadBoreholeVersions,
    importSelectedBoreholes,
    importing,
    importError,
    applyHandoff,
    // Fence diagram — N linii (fixed 5)
    LINE_IDS,
    activeLineId,
    setActiveLineId,
    selectionsByLine,
    selectedBoreholeIdsByLine,
    toggleBoreholeSelectionInLine,
    clearAllSelections,
    onlyExecuted, setOnlyExecuted,
    executedIds,
    loadingExecuted,
  } = useBoreholeBrowser();

  // Paleta linii (fixed dla 5 linii). Spójna między mapą, zakładkami i edytorem.
  const LINE_COLORS = {
    1: { marker: '#a855f7', border: '#7c3aed', stroke: '#7c3aed', bg: 'bg-purple-50', text: 'text-purple-700', ring: 'ring-purple-300', solid: 'bg-purple-600 text-white', label: 'fioletowa' },
    2: { marker: '#0ea5e9', border: '#0284c7', stroke: '#0284c7', bg: 'bg-sky-50',     text: 'text-sky-700',    ring: 'ring-sky-300',    solid: 'bg-sky-600 text-white',    label: 'cyan' },
    3: { marker: '#f97316', border: '#c2410c', stroke: '#c2410c', bg: 'bg-orange-50',  text: 'text-orange-700', ring: 'ring-orange-300', solid: 'bg-orange-600 text-white', label: 'pomarańczowa' },
    4: { marker: '#ec4899', border: '#be185d', stroke: '#be185d', bg: 'bg-pink-50',    text: 'text-pink-700',   ring: 'ring-pink-300',   solid: 'bg-pink-600 text-white',   label: 'różowa' },
    5: { marker: '#84cc16', border: '#4d7c0f', stroke: '#4d7c0f', bg: 'bg-lime-50',    text: 'text-lime-700',   ring: 'ring-lime-300',   solid: 'bg-lime-600 text-white',   label: 'limonkowa' },
    OVERLAP: { marker: '#16a34a', border: '#15803d' }, // otwór w >1 linii
  };

  useEffect(() => {
    if (!projectsLoaded) {
      loadProjects();
    }
  }, [projectsLoaded, loadProjects]);

  // Handoff: dwa zrodla — (a) URL ?handoff=1 + Firestore doc (apka zewn.),
  // (b) location.state.handoffData (lokalne wczytanie z portalu, bez Firestore).
  const [handoffProcessed, setHandoffProcessed] = useState(false);
  useEffect(() => {
    if (handoffProcessed) return;
    if (!handoffFromState && !handoffFlag) return;
    if (!user?.uid) return;
    if (!projectsLoaded) return;

    setHandoffProcessed(true);
    (async () => {
      try {
        let handoff = handoffFromState;
        if (!handoff && handoffFlag) {
          handoff = await readPendingHandoff(user.uid);
        }
        if (handoff && Array.isArray(handoff.items) && handoff.items.length > 0) {
          applyHandoff(handoff);
          if (handoffFlag) {
            await clearPendingHandoff(user.uid);
          }
        } else {
          console.warn('Handoff bez itemow — selekcja niezaaplikowana');
        }
      } catch (err) {
        console.error('Blad odczytu handoffu:', err);
      } finally {
        if (handoffFlag) {
          // Usun ?handoff=1 z URL zeby F5 nie probowal tego drugi raz.
          // Dla handoffFromState NIE czyscimy location.state — pendingProject
          // jest tam potrzebny przy klik "Utworz przekroj".
          navigate('/browse', { replace: true });
        }
      }
    })();
  }, [handoffFlag, handoffFromState, user, projectsLoaded, handoffProcessed, applyHandoff, navigate]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (err) {
      console.error('Blad wylogowania:', err);
    }
  };

  const handleCreateProfile = async () => {
    // Linia 1 (główna) obowiązkowa, pozostałe (2-5) opcjonalne (fence diagram).
    // Tylko Linia 1 idzie do Firestore; reszta w pamięci sesji edytora.
    const importedBoreholes = await importSelectedBoreholes(1);
    if (!importedBoreholes) return;

    // Pętla po liniach 2..5 — zbierz te z ≥2 otworami
    const extraLines = {}; // { 2: [...], 3: [...], 5: [...] } — sparse
    for (const id of LINE_IDS) {
      if (id === 1) continue;
      const sel = selectionsByLine[id] || [];
      if (sel.length < 2) continue;
      const imported = await importSelectedBoreholes(id);
      if (imported && imported.length > 0) extraLines[id] = imported;
    }

    if (pendingProject && user) {
      try {
        const projectData = { boreholes: importedBoreholes };
        if (Object.keys(extraLines).length > 0) projectData.extraLines = extraLines;
        if (pendingProject.scale != null) projectData.scale = pendingProject.scale;
        if (pendingProject.horizontalScale != null) projectData.horizontalScale = pendingProject.horizontalScale;
        const projectId = await createProject(user.uid, pendingProject.name, projectData);
        navigate(`/project/${projectId}`, { replace: true });
      } catch (err) {
        console.error('Blad tworzenia projektu:', err);
        alert('Nie udalo sie utworzyc projektu.');
      }
      return;
    }

    const state = { importedBoreholes };
    if (Object.keys(extraLines).length > 0) state.extraLines = extraLines;
    navigate('/editor', { state });
  };

  // Counts per linia
  const counts = LINE_IDS.reduce((acc, id) => ({ ...acc, [id]: selectedBoreholeIdsByLine[id].size }), {});
  const selectedCount = counts[activeLineId]; // back-compat
  const linesReady = LINE_IDS.filter(id => counts[id] >= 2).length;
  const anyLineHasSelection = LINE_IDS.some(id => counts[id] > 0);

  // Mapa nazw otworów po ID (do panelu wybranych)
  const boreholeNameMap = useMemo(() => {
    const map = {};
    allBoreholes.forEach(b => { map[b.id] = b.nameBorehole || '(brak nazwy)'; });
    return map;
  }, [allBoreholes]);

  // Mapka otworów — przelicz współrzędne 2000 na WGS84, filtruj outlierów (>5km od mediany)
  const mapPoints = useMemo(() => {
    const withCoords = allBoreholes.filter(b => {
      if (b.coordX == null || b.coordY == null) return false;
      if (onlyExecuted && executedIds && !executedIds.has(b.id)) return false;
      return true;
    });
    if (withCoords.length === 0) return null;

    const allPts = withCoords.map(b => {
      const { lat, lng } = pl2000ToWGS84(b.coordX, b.coordY);
      return { id: b.id, name: b.nameBorehole || '(brak nazwy)', lat, lng };
    });

    // Wykrywanie outlierów — otwory z błędną współrzędną (np. zła strefa PL-2000,
    // zamienione X/Y) lądują setki km od reszty i rozwalałyby zoom mapy.
    //
    // UWAGA: próg NIE może być stały. Projekty liniowe (drogi, np. "DK9 odc. II")
    // rozciągają się na kilkanaście km, więc sztywne 5 km wycinało poprawne otwory.
    // Próg skalujemy do rozrzutu samego projektu: 3× odległość percentyla 75,
    // z podłogą 5 km dla ciasnych placów budowy.
    //
    // Outliery NIE są usuwane z mapy — zostają widoczne, tylko nie biorą udziału
    // w fitBounds. Nic nie znika użytkownikowi bez śladu.
    if (allPts.length >= 3) {
      const lats = allPts.map(p => p.lat).sort((a, b) => a - b);
      const lngs = allPts.map(p => p.lng).sort((a, b) => a - b);
      const medLat = lats[Math.floor(lats.length / 2)];
      const medLng = lngs[Math.floor(lngs.length / 2)];

      // ~111km na stopień lat, ~70km na stopień lng (w Polsce)
      const distOf = (p) => {
        const dLat = (p.lat - medLat) * 111;
        const dLng = (p.lng - medLng) * 70;
        return Math.sqrt(dLat * dLat + dLng * dLng);
      };

      const dists = allPts.map(distOf).sort((a, b) => a - b);
      const p75 = dists[Math.floor(dists.length * 0.75)];
      const maxDistKm = Math.max(5, p75 * 3);

      return allPts.map(p => ({ ...p, isOutlier: distOf(p) > maxDistKm }));
    }

    return allPts.map(p => ({ ...p, isOutlier: false }));
  }, [allBoreholes, onlyExecuted, executedIds]);

  const outlierCount = useMemo(
    () => (mapPoints || []).filter(p => p.isOutlier).length,
    [mapPoints]
  );

  // Leaflet map ref i instancja
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef({});
  const clusterGroupRef = useRef(null);
  const profileLineRef = useRef(null);

  // Ref na toggleBoreholeSelection żeby markery nie musiały się odtwarzać
  const toggleSelectionRef = useRef(toggleBoreholeSelection);
  toggleSelectionRef.current = toggleBoreholeSelection;

  // Ref żeby śledzić aktualny mapPoints w efekcie 2 bez dependency
  const mapPointsRef = useRef(null);
  mapPointsRef.current = mapPoints;

  // Efekt 1: Tworzenie mapy i markerów z clusteringiem — TYLKO przy zmianie mapPoints (nowy projekt)
  useEffect(() => {
    if (!mapPoints || mapPoints.length === 0) {
      // Wyczyść markery jeśli mapa istnieje
      if (mapInstanceRef.current) {
        if (clusterGroupRef.current) {
          clusterGroupRef.current.clearLayers();
        }
        markersRef.current = {};
        if (profileLineRef.current) {
          profileLineRef.current.remove();
          profileLineRef.current = null;
        }
      }
      return;
    }

    const L = window.L;
    if (!L || !mapContainerRef.current) return;

    // Tworzenie mapy (raz) — kontener jest zawsze w DOM
    if (!mapInstanceRef.current) {
      mapInstanceRef.current = L.map(mapContainerRef.current, {
        scrollWheelZoom: true,
        zoomControl: true,
      });
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap',
        maxZoom: 19,
      }).addTo(mapInstanceRef.current);
    }

    const map = mapInstanceRef.current;

    // invalidateSize — kontener przechodzi z height:0 na height:400, potrzebuje chwili
    const t1 = setTimeout(() => { if (mapInstanceRef.current) mapInstanceRef.current.invalidateSize(); }, 50);
    const t2 = setTimeout(() => { if (mapInstanceRef.current) mapInstanceRef.current.invalidateSize(); }, 300);
    const cleanup = () => { clearTimeout(t1); clearTimeout(t2); };

    // Usuń starą cluster group
    if (clusterGroupRef.current) {
      map.removeLayer(clusterGroupRef.current);
    }
    markersRef.current = {};

    // Cluster group — grupuje markery przy niskim zoomie
    const useClustering = mapPoints.length > 50 && L.markerClusterGroup;
    const clusterGroup = useClustering
      ? L.markerClusterGroup({
          maxClusterRadius: 40,
          spiderfyOnMaxZoom: true,
          showCoverageOnHover: false,
          disableClusteringAtZoom: 16,
        })
      : L.layerGroup();
    clusterGroupRef.current = clusterGroup;

    // Tooltips permanent tylko jeśli mało punktów (wydajność)
    const permanentTooltips = mapPoints.length <= 80;

    const bounds = [];
    mapPoints.forEach(pt => {
      const icon = L.divIcon({
        className: '',
        html: `<div style="
          width:12px; height:12px;
          background:${pt.isOutlier ? '#f59e0b' : '#3b82f6'};
          border:2px solid ${pt.isOutlier ? '#b45309' : '#2563eb'};
          border-radius:50%; opacity:0.8;
          transform:translate(-50%,-50%);
        "></div>`,
        iconSize: [0, 0],
      });

      const marker = L.marker([pt.lat, pt.lng], { icon })
        .bindTooltip(pt.isOutlier ? `${pt.name} ⚠️ (podejrzane współrzędne)` : pt.name, {
          permanent: permanentTooltips,
          direction: 'right',
          offset: [8, 0],
          className: 'borehole-label',
        });

      marker.on('click', () => {
        toggleSelectionRef.current(pt.id);
      });

      clusterGroup.addLayer(marker);
      markersRef.current[pt.id] = marker;
      // Outliery renderujemy, ale trzymamy poza bounds — inaczej jeden błędny
      // rekord oddala widok tak, że reszta projektu robi się nieczytelna.
      if (!pt.isOutlier) bounds.push([pt.lat, pt.lng]);
    });

    map.addLayer(clusterGroup);

    // fitBounds — tylko tutaj, raz przy załadowaniu punktów
    const fitTo = bounds.length > 0 ? bounds : mapPoints.map(p => [p.lat, p.lng]);
    if (fitTo.length > 0) {
      map.fitBounds(fitTo, { padding: [30, 30], maxZoom: 16 });
    }

    return cleanup;
  }, [mapPoints]); // eslint-disable-line react-hooks/exhaustive-deps

  // Efekt 2: Aktualizacja ikon i linii — przy zmianie zaznaczenia/kolejności (BEZ fitBounds)
  useEffect(() => {
    const L = window.L;
    const pts = mapPointsRef.current;
    if (!L || !mapInstanceRef.current || !pts) return;

    const map = mapInstanceRef.current;

    // Aktualizuj ikony markerów. Stany: w >1 liniach (overlap) / w 1 linii (kolor tej linii) / niezaznaczony
    pts.forEach(pt => {
      const marker = markersRef.current[pt.id];
      if (!marker) return;
      const lineMembership = LINE_IDS.filter(id => selectedBoreholeIdsByLine[id].has(pt.id));
      const isSelected = lineMembership.length > 0;
      let bg = '#3b82f6';
      let border = '#2563eb';
      if (lineMembership.length > 1) {
        bg = LINE_COLORS.OVERLAP.marker;
        border = LINE_COLORS.OVERLAP.border;
      } else if (lineMembership.length === 1) {
        const c = LINE_COLORS[lineMembership[0]];
        bg = c.marker;
        border = c.border;
      }
      try {
        const icon = L.divIcon({
          className: '',
          html: `<div title="${lineMembership.length > 0 ? 'W liniach: ' + lineMembership.join(', ') : ''}" style="
            width:${isSelected ? 16 : 12}px; height:${isSelected ? 16 : 12}px;
            background:${bg};
            border:2px solid ${border};
            border-radius:50%; opacity:${isSelected ? 1 : 0.8};
            transform:translate(-50%,-50%);
          "></div>`,
          iconSize: [0, 0],
        });
        marker.setIcon(icon);
      } catch { /* marker may not be on map yet */ }
    });

    if (clusterGroupRef.current && clusterGroupRef.current.refreshClusters) {
      clusterGroupRef.current.refreshClusters();
    }

    // Aktualizuj polyline — po jednej per linia z ≥2 otworami
    if (profileLineRef.current) {
      if (Array.isArray(profileLineRef.current)) {
        profileLineRef.current.forEach(ln => ln.remove());
      } else {
        profileLineRef.current.remove();
      }
      profileLineRef.current = null;
    }
    const pointsById = {};
    pts.forEach(pt => { pointsById[pt.id] = pt; });
    const lines = [];
    for (const id of LINE_IDS) {
      const sel = selectionsByLine[id] || [];
      if (sel.length < 2) continue;
      const coords = sel.map(s => pointsById[s.id]).filter(Boolean).map(pt => [pt.lat, pt.lng]);
      if (coords.length < 2) continue;
      const ln = L.polyline(coords, { color: LINE_COLORS[id].stroke, weight: 3, opacity: 0.85, dashArray: '8, 6' }).addTo(map);
      lines.push(ln);
    }
    profileLineRef.current = lines.length > 0 ? lines : null;
  }, [selectedBoreholeIdsByLine, selectionsByLine]); // BEZ mapPoints w dependencies!

  // Cleanup mapy przy odmontowaniu komponentu
  useEffect(() => {
    return () => {
      if (clusterGroupRef.current) {
        clusterGroupRef.current.clearLayers();
        clusterGroupRef.current = null;
      }
      markersRef.current = {};
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Naglowek */}
      <div className="app-header">
        <div className="max-w-6xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img src="/logo_giode.png" alt="G-ODE" className="h-6" />
            <div>
              <h1 className="text-xl font-bold text-gray-800">
                Wyszukiwarka Otworów
              </h1>
              <p className="text-xs text-gray-400">Generator przekrojów geologicznych</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/interpolation')}
              className="btn-brand-outline flex flex-col items-center justify-center gap-1 w-[110px] h-[56px] text-center leading-tight"
            >
              <Layers size={16} />
              Interpolacja
            </button>
            <button
              onClick={() => navigate('/projects')}
              className="btn-brand-outline flex flex-col items-center justify-center gap-1 w-[110px] h-[56px] text-center leading-tight"
            >
              <FolderOpen size={16} />
              Moje przekroje
            </button>
            <button
              onClick={() => navigate('/editor')}
              className="btn-brand-outline flex items-center justify-center w-[110px] h-[56px] text-center leading-tight"
            >
              Edytor (pusty)
            </button>
            <span className="text-sm text-gray-500">{user?.email}</span>
            <button
              onClick={handleLogout}
              className="btn-brand-outline flex items-center gap-1"
            >
              <LogOut size={16} />
              Wyloguj
            </button>
          </div>
        </div>
      </div>

      {/* Glowna zawartosc */}
      <div className="max-w-6xl mx-auto px-6 py-6">

        {/* Panel wybranych otworów z odległościami */}
        {anyLineHasSelection && (
          <div className="bg-white rounded-lg shadow-lg p-4 mb-4">
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
              {/* 5 zakładek — klik przełącza aktywną linię */}
              <div className="flex items-center gap-1 flex-wrap">
                <span className="text-sm font-semibold text-gray-700 mr-2">Wybrane otwory:</span>
                {LINE_IDS.map(id => {
                  const c = LINE_COLORS[id];
                  const isActive = activeLineId === id;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setActiveLineId(id)}
                      className={`px-2.5 py-1.5 rounded text-xs font-medium transition border ${
                        isActive
                          ? `${c.solid} border-current ring-2 ${c.ring}`
                          : counts[id] > 0
                            ? `${c.bg} ${c.text} border-current hover:opacity-80`
                            : `bg-gray-50 text-gray-400 border-gray-200 hover:bg-gray-100`
                      }`}
                      title={`Linia ${id} (${c.label})${counts[id] >= 2 ? ' — gotowa do przekroju' : ''}`}
                    >
                      L{id} ({counts[id]})
                    </button>
                  );
                })}
                {linesReady >= 2 && (
                  <span className="text-xs text-green-700 ml-2 px-2 py-0.5 bg-green-50 rounded border border-green-200">
                    🔀 Fence diagram: {linesReady} linii gotowych
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={clearSelection}
                  className="px-3 py-1.5 rounded text-xs bg-gray-100 hover:bg-gray-200 text-gray-600"
                  title={`Wyczyść Linię ${activeLineId}`}
                >
                  Wyczyść L{activeLineId}
                </button>
                {anyLineHasSelection && LINE_IDS.filter(id => counts[id] > 0).length > 1 && (
                  <button
                    onClick={clearAllSelections}
                    className="px-3 py-1.5 rounded text-xs bg-gray-100 hover:bg-gray-200 text-gray-600"
                    title="Wyczyść wszystkie linie"
                  >
                    Wyczyść wszystkie
                  </button>
                )}
                <button
                  onClick={handleCreateProfile}
                  disabled={importing || counts[1] < 2}
                  className="px-4 py-1.5 rounded text-xs font-medium bg-brand-700 text-white hover:bg-brand-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  title={counts[1] < 2 ? 'Linia 1 musi mieć min. 2 otwory' : ''}
                >
                  {importing
                    ? 'Pobieranie...'
                    : pendingProject
                      ? `Utwórz projekt "${pendingProject.name}"`
                      : linesReady >= 2
                        ? `Utwórz krzyżowy przekrój (${linesReady} linii)`
                        : 'Utwórz przekrój'}
                </button>
              </div>
            </div>

            {importError && (
              <div className="mb-3 p-2 bg-red-50 rounded text-xs text-red-700">{importError}</div>
            )}

            {/* Tabela wybranych otworów */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-gray-500 border-b">
                    <th className="text-left py-1.5 px-2 w-8">#</th>
                    <th className="text-left py-1.5 px-2">Otwór</th>
                    <th className="text-left py-1.5 px-2 w-56">Wersja</th>
                    <th className="text-center py-1.5 px-2 w-32">Odl. od poprz. (m)</th>
                    <th className="text-center py-1.5 px-2 w-24">Suma (m)</th>
                    <th className="text-center py-1.5 px-2 w-24">Kolejność</th>
                    <th className="text-center py-1.5 px-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {computedDistances.map((item, index) => (
                    <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-1.5 px-2 text-gray-400 text-xs">{index + 1}</td>
                      <td className="py-1.5 px-2 font-medium">{boreholeNameMap[item.id]}</td>
                      <td className="py-1.5 px-2">
                        <VersionPicker
                          boreholeId={item.id}
                          selectedVersionId={item.userDataIdOverride}
                          versionsState={versionsByBoreholeId[item.id]}
                          onOpen={() => loadBoreholeVersions(item.id)}
                          onChange={(versionId) => setBoreholeUserDataVersion(index, versionId)}
                        />
                      </td>
                      <td className="py-1.5 px-2 text-center">
                        {index === 0 ? (
                          <span className="text-gray-300">—</span>
                        ) : (
                          <div className="flex items-center justify-center gap-1">
                            <input
                              type="number"
                              min="0"
                              step="0.1"
                              value={item.distanceOverride != null ? item.distanceOverride : item.autoDistance}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value);
                                setBoreholeDistance(index, isNaN(val) ? null : val);
                              }}
                              className={`w-20 border rounded px-1.5 py-0.5 text-xs text-center ${
                                item.distanceOverride != null ? 'border-orange-300 bg-orange-50' : ''
                              }`}
                            />
                            {item.distanceOverride != null && (
                              <button
                                onClick={() => setBoreholeDistance(index, null)}
                                className="text-gray-400 hover:text-gray-600"
                                title="Przywróć auto"
                              >
                                <X size={12} />
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="py-1.5 px-2 text-center text-xs text-gray-500">
                        {item.cumulativeDistance.toFixed(1)}
                      </td>
                      <td className="py-1.5 px-2 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => moveBoreholeUp(index)}
                            disabled={index === 0}
                            className={`p-0.5 rounded ${index === 0 ? 'text-gray-200' : 'text-gray-500 hover:text-brand-700 hover:bg-brand-50'}`}
                          >
                            <ArrowUp size={14} />
                          </button>
                          <button
                            onClick={() => moveBoreholeDown(index)}
                            disabled={index === orderedSelection.length - 1}
                            className={`p-0.5 rounded ${index === orderedSelection.length - 1 ? 'text-gray-200' : 'text-gray-500 hover:text-brand-700 hover:bg-brand-50'}`}
                          >
                            <ArrowDown size={14} />
                          </button>
                        </div>
                      </td>
                      <td className="py-1.5 px-2 text-center">
                        <button
                          onClick={() => toggleBoreholeSelection(item.id)}
                          className="text-gray-300 hover:text-red-500"
                          title="Usuń z wyboru"
                        >
                          <X size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="text-xs text-gray-400 mt-2">
              Odległości wyliczane automatycznie ze współrzędnych XY. Wpisz ręcznie aby nadpisać (pomarańczowe pole).
            </div>
          </div>
        )}

        {/* Toolbar — widoczny gdy brak zaznaczonych */}
        {!anyLineHasSelection && (
          <div className="bg-white rounded-lg shadow-lg p-4 mb-4">
            <div className="flex gap-2 items-center flex-wrap">
              <button
                onClick={refreshProjects}
                className="px-3 py-2 rounded text-sm bg-gray-200 hover:bg-gray-300"
                disabled={loadingProjects}
              >
                Odśwież listę
              </button>
              <div className="flex-1" />
              <span className="text-sm text-gray-400">
                {pendingProject
                  ? `Nowy projekt: "${pendingProject.name}" — zaznacz otwory`
                  : 'Zaznacz otwory aby utworzyć przekrój. Opcjonalnie wybierz Linię 2 dla widoku krzyżowego (fence diagram).'}
              </span>
            </div>
          </div>
        )}

        {/* Mapka na górze — kontener zawsze w DOM, ukryty przez overflow+height */}
        {(() => {
          const mapVisible = selectedProject && !loadingBoreholes && mapPoints && mapPoints.length > 0;
          return (
            <div style={mapVisible ? {} : { height: 0, overflow: 'hidden', margin: 0, padding: 0 }}
              className={mapVisible ? 'bg-white rounded-lg shadow-lg p-4 mb-4' : ''}>
              {mapVisible && (
                <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
                  <MapPin size={14} className="text-brand-700" />
                  Lokalizacja otworów
                  {onlyExecuted && (
                    <span className="ml-auto font-normal text-xs text-emerald-600">
                      ✓ tylko wykonane
                    </span>
                  )}
                </h3>
              )}
              <div
                ref={mapContainerRef}
                style={{ height: 400, width: '100%', borderRadius: 8 }}
              />
              {mapVisible && (
                <div className="text-xs text-gray-400 mt-1">
                  Kliknij punkt aby zaznaczyć/odznaczyć otwór. Zaznaczone = zielone.
                  {outlierCount > 0 && (
                    <span className="text-amber-600 ml-1">
                      {' '}⚠️ {outlierCount} {outlierCount === 1 ? 'otwór odbiega' : 'otworów odbiega'} od
                      reszty projektu (pomarańczowe) — sprawdź współrzędne. Widok dopasowano bez nich.
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {/* Dwie kolumny: projekty + otwory */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Lewa kolumna: lista projektów */}
          <div className="bg-white rounded-lg shadow-lg p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Projekty</h3>
            <div className="mb-3 relative">
              <input
                type="text"
                placeholder="Filtruj projekty..."
                value={projectFilter}
                onChange={(e) => setProjectFilter(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Escape') setProjectFilter(''); }}
                className="w-full border rounded px-3 py-2 pr-8 text-sm"
              />
              {projectFilter && (
                <button
                  type="button"
                  onClick={() => setProjectFilter('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-gray-200 text-gray-500"
                  aria-label="Wyczyść filtr"
                  title="Wyczyść (Esc)"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            {loadingProjects && (
              <div className="text-gray-500 text-sm py-8 text-center">Ładowanie projektów...</div>
            )}
            {projectsError && (
              <div className="text-red-500 text-sm py-2">{projectsError}</div>
            )}

            {!loadingProjects && projectsLoaded && (
              <div className="space-y-1 max-h-[500px] overflow-y-auto">
                {projects.length === 0 ? (
                  <div className="text-gray-400 text-sm py-4 text-center">
                    {projectFilter ? 'Brak projektów pasujących do filtra.' : 'Brak projektów w bazie.'}
                  </div>
                ) : (
                  projects.map(project => (
                    <button
                      key={project.id}
                      onClick={() => selectProject(project)}
                      className={`w-full flex justify-between items-center px-4 py-3 text-left text-sm rounded transition ${
                        selectedProject?.id === project.id
                          ? 'bg-brand-50 text-brand-800 font-semibold'
                          : 'hover:bg-gray-100'
                      }`}
                    >
                      <span>{project.title}</span>
                      {selectedProject?.id === project.id
                        ? <ChevronUp size={16} />
                        : <ChevronDown size={16} />
                      }
                    </button>
                  ))
                )}
                {projects.length > 0 && (
                  <div className="text-gray-400 text-xs pt-2 border-t mt-2">
                    Łącznie projektów: {projects.length}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Prawa kolumna: lista otworów */}
          <div className="bg-white rounded-lg shadow-lg p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
              <span>
                Otwory
                {selectedProject && (
                  <span className="font-normal text-gray-400 ml-2">— {selectedProject.title}</span>
                )}
              </span>
              {selectedProject && allBoreholes.length > 0 && (
                <label
                  className="ml-auto flex items-center gap-1.5 font-normal text-xs text-gray-600 cursor-pointer whitespace-nowrap"
                  title="Pokazuj tylko otwory, które mają wpis w UserData — czyli faktycznie opisane"
                >
                  <input
                    type="checkbox"
                    checked={onlyExecuted}
                    onChange={(e) => setOnlyExecuted(e.target.checked)}
                    className="w-3.5 h-3.5"
                  />
                  Tylko wykonane
                  {loadingExecuted && <span className="text-gray-400">(sprawdzam…)</span>}
                  {onlyExecuted && !loadingExecuted && executedIds && (
                    <span className="text-gray-400">({executedIds.size}/{allBoreholes.length})</span>
                  )}
                </label>
              )}
            </h3>

            {!selectedProject ? (
              <div className="text-gray-400 text-sm py-8 text-center">
                Wybierz projekt z listy po lewej stronie.
              </div>
            ) : loadingBoreholes ? (
              <div className="text-gray-400 text-sm py-8 text-center">Ładowanie otworów...</div>
            ) : boreholesError ? (
              <div className="text-red-400 text-sm py-2">{boreholesError}</div>
            ) : allBoreholes.length === 0 ? (
              <div className="text-gray-400 text-sm py-8 text-center">Brak otworów w tym projekcie.</div>
            ) : (
              <>
                <div className="mb-3 relative">
                  <input
                    type="text"
                    placeholder="Filtruj otwory..."
                    value={boreholeFilter}
                    onChange={(e) => setBoreholeFilter(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Escape') setBoreholeFilter(''); }}
                    className="w-full border rounded px-2 py-2 pr-8 text-sm"
                  />
                  {boreholeFilter && (
                    <button
                      type="button"
                      onClick={() => setBoreholeFilter('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-gray-200 text-gray-500"
                      aria-label="Wyczyść filtr"
                      title="Wyczyść (Esc)"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
                <div className="text-xs text-gray-500 mb-2 px-1">
                  Klik dodaje do <span className={`font-semibold ${LINE_COLORS[activeLineId].text}`}>
                    Linii {activeLineId} ({LINE_COLORS[activeLineId].label})
                  </span>. Pigułki 1-5 toggle w konkretnej linii bez zmiany aktywnej.
                </div>
                <div className="space-y-1 max-h-[500px] overflow-y-auto">
                  {boreholes.length === 0 && (
                    <div className="text-gray-400 text-sm py-6 text-center">
                      {boreholeFilter && onlyExecuted
                        ? <>Żaden wykonany otwór nie pasuje do filtra „{boreholeFilter}".</>
                        : boreholeFilter
                          ? <>Brak otworów pasujących do filtra „{boreholeFilter}".</>
                          : <>Żaden otwór w tym projekcie nie ma jeszcze wpisu w UserData.</>}
                      {boreholeFilter && (
                        <button
                          type="button"
                          onClick={() => setBoreholeFilter('')}
                          className="block mx-auto mt-2 text-brand-700 hover:underline text-xs"
                        >
                          Wyczyść filtr
                        </button>
                      )}
                      {onlyExecuted && (
                        <button
                          type="button"
                          onClick={() => setOnlyExecuted(false)}
                          className="block mx-auto mt-2 text-brand-700 hover:underline text-xs"
                        >
                          Pokaż też niewykonane
                        </button>
                      )}
                    </div>
                  )}
                  {boreholes.map(borehole => {
                    const membership = LINE_IDS.filter(id => selectedBoreholeIdsByLine[id].has(borehole.id));
                    const inActive = membership.includes(activeLineId);
                    const activeColors = LINE_COLORS[activeLineId];
                    return (
                      <label
                        key={borehole.id}
                        className={`flex items-center px-3 py-2 text-sm rounded cursor-pointer transition border ${
                          inActive
                            ? `${activeColors.bg} border-current ${activeColors.text}`
                            : membership.length > 0
                              ? 'bg-gray-50 border-gray-300'
                              : 'bg-gray-50 hover:bg-gray-100 border-transparent'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={inActive}
                          onChange={() => toggleBoreholeSelection(borehole.id)}
                          className="mr-3 w-4 h-4"
                          style={{ accentColor: activeColors.marker }}
                        />
                        <span className={`flex-1 ${inActive ? 'font-medium' : ''}`}>
                          {borehole.nameBorehole || '(brak nazwy)'}
                          {executedIds && executedIds.has(borehole.id) && (
                            <span className="ml-1.5 text-emerald-600" title="Wykonany — ma wpis w UserData">✓</span>
                          )}
                        </span>
                        {/* Pigułki 1..5 — toggle w konkretnej linii niezależnie od aktywnej */}
                        {LINE_IDS.map(id => {
                          const inLine = selectedBoreholeIdsByLine[id].has(borehole.id);
                          const c = LINE_COLORS[id];
                          return (
                            <button
                              key={id}
                              type="button"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleBoreholeSelectionInLine(borehole.id, id); }}
                              className={`ml-1 px-1.5 py-0.5 rounded text-xs font-semibold transition border ${
                                inLine ? `${c.solid} border-current` : `${c.bg} ${c.text} border-current hover:opacity-80`
                              }`}
                              title={`Toggle w Linii ${id} (${c.label})`}
                            >
                              {id}
                            </button>
                          );
                        })}
                        {borehole.coordZ != null && (
                          <span className="text-xs text-gray-400 ml-2">
                            {borehole.coordZ} m n.p.m.
                          </span>
                        )}
                      </label>
                    );
                  })}
                  {boreholes.length > 0 && (
                    <div className="text-gray-400 text-xs pt-2">
                      Znaleziono: {boreholes.length} otwór(ów)
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
